import { Trophy } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ElectionResults } from '@/types'

interface WinnerBannerProps {
  results: ElectionResults
  className?: string
}

export function WinnerBanner({ results, className }: WinnerBannerProps) {
  const winnerResult = results.party_results.find(
    (p) => p.party_id === results.winner_party_id
  )

  if (!winnerResult) {
    return (
      <div className={cn('rounded-lg border bg-muted/50 p-6 text-center', className)}>
        <p className="text-lg font-medium">No clear winner — hung parliament</p>
        <p className="text-muted-foreground text-sm mt-1">
          Coalition negotiations required
        </p>
      </div>
    )
  }

  const governmentLabel = results.majority_achieved ? 'Majority' : 'Coalition/Minority'

  return (
    <div
      className={cn('rounded-lg border p-6', className)}
      style={{ borderLeftWidth: 4, borderLeftColor: winnerResult.party_color }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full"
          style={{ backgroundColor: winnerResult.party_color + '20' }}
        >
          <Trophy className="h-5 w-5" style={{ color: winnerResult.party_color }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold">{winnerResult.party_name} wins</h2>
            <Badge variant="outline">{governmentLabel}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            {winnerResult.seats_won} of {results.total_seats} seats
            ({winnerResult.vote_share.toFixed(1)}% vote share)
            {' · '}Turnout: {results.turnout_percentage.toFixed(1)}%
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tabular-nums">{winnerResult.seats_won}</div>
          <p className="text-muted-foreground text-xs">
            seats (need {Math.floor(results.total_seats / 2) + 1} for majority)
          </p>
        </div>
      </div>
    </div>
  )
}
